import dayjs from "dayjs";
import React, { useMemo, useState, useEffect } from "react";
import { Select } from "ui-components";
import { TooltipComponent } from "ui-components/src/components/Tooltip/TooltipComponent";

export interface IQuarterOption {
  id: string;
  title: string;
  year: number;
  quarter: number;
}

interface IHiringPipelineFilterProps {
  onFilterChange: (from: IQuarterOption, to: IQuarterOption) => void;
}

export function HiringPipelineFilter({
  onFilterChange,
}: IHiringPipelineFilterProps): JSX.Element {
  const currentYear = dayjs().year();
  const currentQuarter = Math.ceil((dayjs().month() + 1) / 3);

  const allQuarters: IQuarterOption[] = useMemo(() => {
    const quarters: IQuarterOption[] = [];
    const quarterMonths = ["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"];
    for (let year = 2000; year <= currentYear; year += 1) {
      for (let q = 1; q <= 4; q += 1) {
        quarters.push({
          id: `Q${q}-${year}`,
          title: `Q${q} ${year} (${quarterMonths[q - 1]})`,
          year,
          quarter: q,
        });
      }
    }
    return quarters;
  }, [currentYear]);

  const [fromQuarter, setFromQuarter] = useState<IQuarterOption>(
    allQuarters.find((q) => q.year === currentYear && q.quarter === 1)!
  );
  const [toQuarter, setToQuarter] = useState<IQuarterOption>(
    allQuarters.find(
      (q) => q.year === currentYear && q.quarter === currentQuarter
    )!
  );

  const toOptions = useMemo(
    () =>
      allQuarters.filter(
        (q) =>
          q.year > fromQuarter.year ||
          (q.year === fromQuarter.year && q.quarter >= fromQuarter.quarter)
      ),
    [allQuarters, fromQuarter]
  );

  // 🔁 Notify parent whenever filters change
  useEffect(() => {
    onFilterChange(fromQuarter, toQuarter);
  }, [fromQuarter, toQuarter, onFilterChange]);

  return (
    <div className="w-full h-full p-4 bg-[#FDF1EC] flex flex-col justify-start items-start">
      <div className="w-full flex flex-col justify-start items-start gap-4">
        <div className="w-full flex flex-col sm:flex-row justify-between sm:items-center">
          <div className="w-full sm:w-auto mb-2 sm:mb-0">
            <div className="text-black text-base font-semibold">
              Hiring data period:
            </div>
          </div>
          <div className="flex justify-start w-full sm:w-auto">
            <div className="w-[198.68px] h-[50px] p-1 bg-white rounded-[10px] border border-[#FFD4C4] flex hover:cursor-pointer">
              <TooltipComponent
                text="Monthly reporting available soon"
                icon={
                  <div className="w-[89.55px] h-10 relative rounded-lg">
                    <div className="absolute left-4 top-[7px] text-[#535455] text-base font-normal leading-6">
                      Monthly
                    </div>
                  </div>
                }
              />
              <div className="flex-1 h-10 relative bg-[#EB773D] rounded-lg">
                <div className="absolute left-4 top-[7px] text-black text-base font-normal leading-6">
                  Quarterly
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full flex flex-col md:flex-row justify-start gap-3">
          <Select
            options={allQuarters.map((q) => ({
              id: q.id,
              title: q.title,
            }))}
            value={fromQuarter.id}
            onSelection={(selectedId) => {
              const selected = allQuarters.find((q) => q.id === selectedId);
              if (selected) {
                setFromQuarter(selected);
                if (
                  selected.year > toQuarter.year ||
                  (selected.year === toQuarter.year &&
                    selected.quarter > toQuarter.quarter)
                ) {
                  setToQuarter(selected);
                }
              }
            }}
            placeholder="Select from quarter"
            className="rounded-none border-0 bg-gray-100 w-full md:w-[170px]"
          />

          <div className="hidden md:block w-[15.16px] h-6 relative mt-2">
            <div className="absolute top-[-1px] left-0 text-[#4A5565] text-base font-normal leading-6">
              to
            </div>
          </div>
          <Select
            options={toOptions.map((q) => ({
              id: q.id,
              title: q.title,
            }))}
            value={toQuarter.id}
            onSelection={(selectedId) => {
              const selected = allQuarters.find((q) => q.id === selectedId);
              if (selected) {
                setToQuarter(selected);
              }
            }}
            placeholder="Select to quarter"
            className="rounded-none border-0 bg-gray-100 w-full md:w-[170px]"
          />
        </div>
      </div>
    </div>
  );
}
